<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RrButton, RrCheckbox, RrForm, RrInput, RrModal, RrRadio, RrSelect, RrSpin, RrTabs, RrTag } from '@xjlv2/ui'
import type { ApiClient } from '@xjlv2/api-client'
import { Plus, Trash2 } from '@lucide/vue'
import type {
  AiMaterialCapability,
  AiProviderConfig,
  AiProviderModel,
  SaveAiProviderRequest
} from '../../types'

const props = defineProps<{
  open: boolean
  createApiClient: () => ApiClient
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
  notice: [message: string]
  error: [message: string]
  changed: []
}>()

type ModelCapabilityFilter = 'ALL' | AiMaterialCapability

const capabilityLabels: Record<AiMaterialCapability, string> = {
  TEXT: '文稿',
  IMAGE: '图片',
  AUDIO: '音频',
  VIDEO: '视频'
}

const capabilityOrder: AiMaterialCapability[] = ['TEXT', 'IMAGE', 'AUDIO', 'VIDEO']

const providerTypeOptions = [
  { label: 'OpenAI Compatible', value: 'OPENAI_COMPATIBLE' },
  { label: 'DashScope (通义千问)', value: 'DASHSCOPE' },
  { label: 'HunYuan (混元)', value: 'HUNYUAN' },
  { label: 'MiniMax', value: 'MINIMAX' }
]

const capabilitySelectOptions = capabilityOrder.map((capability) => ({
  label: capabilityLabels[capability],
  value: capability
}))

const providers = ref<AiProviderConfig[]>([])
const loadingProviders = ref(false)
const testingProviderId = ref<number | null>(null)
const deletingProviderId = ref<number | null>(null)
const savingProvider = ref(false)
const editingProviderId = ref<number | null>(null)
const modelCapabilityFilter = ref<ModelCapabilityFilter>('ALL')
const providerToDelete = ref<AiProviderConfig | null>(null)
const providerForm = ref<SaveAiProviderRequest>(emptyProviderForm())

const isCurrentProviderBuiltIn = computed(() => {
  if (editingProviderId.value == null) return false
  return providers.value.find((item) => item.id === editingProviderId.value)?.builtIn === true
})

const canSaveProvider = computed(() => {
  return (
    providerForm.value.name.trim().length > 0
    && providerForm.value.providerCode.trim().length > 0
    && providerForm.value.modelCatalog.length > 0
    && providerForm.value.modelCatalog.every((item) => item.name.trim().length > 0)
    && (editingProviderId.value != null || (providerForm.value.apiKey?.trim().length ?? 0) > 0)
  )
})

const visibleProviderModels = computed(() => {
  return providerForm.value.modelCatalog
    .map((model, index) => ({ model, index }))
    .filter(({ model }) => modelCapabilityFilter.value === 'ALL' || model.capability === modelCapabilityFilter.value)
})

const providerCapabilityCounts = computed(() => {
  return capabilityOrder.reduce((acc, capability) => {
    acc[capability] = providerForm.value.modelCatalog.filter((model) => model.capability === capability).length
    return acc
  }, {} as Record<AiMaterialCapability, number>)
})

const capabilityTabItems = computed(() => [
  { key: 'ALL', label: `全部 (${providerForm.value.modelCatalog.length})` },
  ...capabilityOrder.map((capability) => ({
    key: capability,
    label: `${capabilityLabels[capability]} (${providerCapabilityCounts.value[capability] ?? 0})`
  }))
])

function providerModelCatalog(provider: AiProviderConfig): AiProviderModel[] {
  if (Array.isArray(provider.modelCatalog) && provider.modelCatalog.length > 0) {
    return provider.modelCatalog
  }
  const primaryCapability: AiMaterialCapability = provider.capabilities?.[0] ?? 'TEXT'
  return (provider.models || []).map((name, index) => ({
    name,
    capability: primaryCapability,
    isDefault: index === 0
  }))
}

function setDefaultModel(targetModel: AiProviderModel) {
  for (const model of providerForm.value.modelCatalog) {
    if (model.capability === targetModel.capability) {
      model.isDefault = model === targetModel
    }
  }
}

function onModelCapabilityChange(model: AiProviderModel, newCap: AiMaterialCapability) {
  const oldCap = model.capability
  model.capability = newCap
  if (model.isDefault) {
    const nextOld = providerForm.value.modelCatalog.find((m) => m !== model && m.capability === oldCap)
    if (nextOld) nextOld.isDefault = true
  }
  const hasDefaultInNew = providerForm.value.modelCatalog.some((m) => m !== model && m.capability === newCap && m.isDefault)
  model.isDefault = !hasDefaultInNew
}

function emptyProviderForm(): SaveAiProviderRequest {
  return {
    name: '',
    providerCode: 'OPENAI_COMPATIBLE',
    baseUrl: '',
    apiKey: '',
    models: [],
    modelCatalog: [{ name: '', capability: 'TEXT', isDefault: true }],
    capabilities: ['TEXT'],
    enabled: true,
    isDefault: false
  }
}

function startCreateProvider() {
  editingProviderId.value = null
  providerForm.value = emptyProviderForm()
  modelCapabilityFilter.value = 'ALL'
}

function startEditProvider(provider: AiProviderConfig) {
  editingProviderId.value = provider.id
  const modelCatalog = JSON.parse(JSON.stringify(providerModelCatalog(provider))) as AiProviderModel[]
  providerForm.value = {
    name: provider.name,
    providerCode: provider.providerCode,
    baseUrl: provider.baseUrl ?? '',
    apiKey: '',
    models: modelCatalog.map((model) => model.name),
    modelCatalog,
    capabilities: [...new Set(modelCatalog.map((model) => model.capability))],
    enabled: provider.enabled,
    isDefault: provider.isDefault
  }
  modelCapabilityFilter.value = 'ALL'
}

function addProviderModel() {
  const capability = modelCapabilityFilter.value === 'ALL' ? 'TEXT' : modelCapabilityFilter.value
  const hasDefault = providerForm.value.modelCatalog.some((model) => model.capability === capability && model.isDefault)
  providerForm.value.modelCatalog.push({ name: '', capability, isDefault: !hasDefault })
}

function removeProviderModel(index: number) {
  const removed = providerForm.value.modelCatalog[index]
  providerForm.value.modelCatalog.splice(index, 1)
  if (removed?.isDefault) {
    const next = providerForm.value.modelCatalog.find((m) => m.capability === removed.capability)
    if (next) next.isDefault = true
  }
}

async function loadProviders() {
  loadingProviders.value = true
  try {
    const list = await props.createApiClient().get<AiProviderConfig[]>('/api/saas/system/ai/providers')
    providers.value = list
  } catch (err) {
    emit('error', err instanceof Error ? err.message : 'AI 提供商加载失败')
  } finally {
    loadingProviders.value = false
  }
}

async function saveProvider() {
  if (savingProvider.value || !canSaveProvider.value) return
  savingProvider.value = true
  const modelCatalog = providerForm.value.modelCatalog.map((model) => ({
    name: model.name.trim(),
    capability: model.capability,
    isDefault: Boolean(model.isDefault)
  }))
  const payload: SaveAiProviderRequest = {
    ...providerForm.value,
    name: providerForm.value.name.trim(),
    providerCode: providerForm.value.providerCode.trim(),
    baseUrl: providerForm.value.baseUrl?.trim(),
    apiKey: providerForm.value.apiKey?.trim() || undefined,
    modelCatalog,
    models: modelCatalog.map((model) => model.name),
    capabilities: [...new Set(modelCatalog.map((model) => model.capability))]
  }
  try {
    const api = props.createApiClient()
    if (editingProviderId.value == null) {
      await api.post('/api/saas/system/ai/providers', payload)
    } else {
      await api.put(`/api/saas/system/ai/providers/${editingProviderId.value}`, payload)
    }
    emit('notice', editingProviderId.value == null ? 'AI 提供商已添加。' : 'AI 提供商配置已保存。')
    emit('changed')
    startCreateProvider()
    await loadProviders()
  } catch (err) {
    emit('error', err instanceof Error ? err.message : 'AI 提供商保存失败')
  } finally {
    savingProvider.value = false
  }
}

async function testProvider(provider: AiProviderConfig) {
  if (testingProviderId.value != null) return
  testingProviderId.value = provider.id
  try {
    const result = await props.createApiClient().post<{ success: boolean; status?: string; message?: string; latencyMs?: number }>(
      `/api/saas/system/ai/providers/${provider.id}/test`,
      {}
    )
    if (result.status === 'CONFIGURED') {
      emit('notice', result.message || '配置有效；媒体能力需对应厂商适配器执行，未执行在线测试')
    } else if (result.status === 'CONNECTED' || result.success) {
      const message = `${result.message || '连接成功'}${result.latencyMs == null ? '' : ` · ${result.latencyMs} ms`}`
      emit('notice', message)
    } else {
      emit('error', result.message || '连接失败，请检查配置')
    }
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '连接测试失败')
  } finally {
    testingProviderId.value = null
  }
}

function promptDeleteProvider(provider: AiProviderConfig) {
  if (deletingProviderId.value != null) return
  providerToDelete.value = provider
}

async function confirmDeleteProvider() {
  if (!providerToDelete.value) return
  const provider = providerToDelete.value
  deletingProviderId.value = provider.id
  try {
    await props.createApiClient().delete(`/api/saas/system/ai/providers/${provider.id}`)
    emit('notice', 'AI 提供商已删除。')
    emit('changed')
    providerToDelete.value = null
    startCreateProvider()
    await loadProviders()
  } catch (err) {
    emit('error', err instanceof Error ? err.message : 'AI 提供商删除失败')
  } finally {
    deletingProviderId.value = null
  }
}

watch(() => props.open, (isOpen) => {
  if (isOpen) {
    startCreateProvider()
    void loadProviders()
  }
})

onMounted(() => {
  if (props.open) {
    startCreateProvider()
    void loadProviders()
  }
})
</script>

<template>
  <RrModal
    :open="open"
    title="AI 提供商配置"
    :width="960"
    :footer="null"
    class="ai-providers-modal"
    @update:open="(val) => emit('update:open', val)"
  >
    <div class="providers-modal-body">
      <!-- 左侧提供商列表 -->
      <aside class="providers-list-panel">
        <div class="list-head">
          <strong>已配置提供商</strong>
          <RrButton size="small" type="primary" @click="startCreateProvider">
            <Plus :size="14" /> 新增
          </RrButton>
        </div>

        <div v-if="loadingProviders" class="loading-state">
          <RrSpin tip="正在加载提供商..." />
        </div>
        <p v-else-if="providers.length === 0" class="empty-tip">暂未配置任何提供商</p>
        <div v-else class="provider-items">
          <div
            v-for="provider in providers"
            :key="provider.id"
            class="provider-item"
            :class="{ active: editingProviderId === provider.id, disabled: provider.builtIn }"
            role="button"
            :tabindex="provider.builtIn ? -1 : 0"
            @click="!provider.builtIn && startEditProvider(provider)"
            @keydown.enter="!provider.builtIn && startEditProvider(provider)"
          >
            <span class="provider-badge">{{ provider.name.slice(0, 1).toUpperCase() }}</span>
            <div class="provider-meta">
              <strong :title="provider.name">{{ provider.name }}</strong>
              <small>{{ provider.providerCode }} · {{ providerModelCatalog(provider).length }} 模型</small>
            </div>
            <div class="provider-status-col">
              <RrTag size="small" :color="provider.enabled ? 'success' : 'default'">
                {{ provider.enabled ? '已启用' : '已停用' }}
              </RrTag>
              <RrTag v-if="provider.isDefault" size="small" color="processing">默认</RrTag>
              <RrTag v-if="provider.hasApiKey === false" size="small" color="warning">未配Key</RrTag>
            </div>
            <RrButton
              v-if="provider.builtIn"
              size="small"
              class="test-btn"
              :disabled="testingProviderId != null"
              :loading="testingProviderId === provider.id"
              @click.stop="testProvider(provider)"
            >
              {{ testingProviderId === provider.id ? '测试中...' : '测试' }}
            </RrButton>
          </div>
        </div>
      </aside>

      <!-- 右侧表单配置 -->
      <main class="provider-form-panel">
        <RrForm class="provider-form" @submit.prevent="saveProvider">
          <div class="form-head">
            <strong>{{ editingProviderId == null ? '新增提供商' : '编辑提供商' }}</strong>
            <RrButton
              v-if="editingProviderId != null"
              size="small"
              type="primary"
              :danger="true"
              :disabled="savingProvider || isCurrentProviderBuiltIn"
              @click="promptDeleteProvider(providers.find(p => p.id === editingProviderId)!)"
            >
              删除提供商
            </RrButton>
          </div>

          <div class="form-grid">
            <label>
              <span class="label-text">显示名称 <em class="required-star">*</em></span>
              <RrInput v-model:value="providerForm.name" placeholder="例如 OpenAI 生产环境" required />
            </label>
            <label>
              <span class="label-text">提供商类型 <em class="required-star">*</em></span>
              <RrSelect v-model:value="providerForm.providerCode" :options="providerTypeOptions" />
            </label>
            <label class="grid-span-2">
              <span class="label-text">Base URL</span>
              <RrInput v-model:value="providerForm.baseUrl" placeholder="https://api.example.com/v1" />
            </label>
            <label class="grid-span-2">
              <span class="label-text">
                API Key
                <em v-if="editingProviderId == null" class="required-star">*</em>
              </span>
              <RrInput
                v-model:value="providerForm.apiKey"
                type="password"
                :placeholder="editingProviderId == null ? '请输入 API Key（保存后安全隐藏）' : '保持为空则沿用原密钥'"
              />
            </label>
          </div>

          <!-- 模型清单 -->
          <section class="model-catalog-section">
            <div class="model-section-head">
              <strong>模型清单</strong>
              <RrButton size="small" @click="addProviderModel">
                <Plus :size="14" /> 添加模型
              </RrButton>
            </div>

            <RrTabs
              :active-key="modelCapabilityFilter"
              size="small"
              :items="capabilityTabItems"
              class="capability-tabs"
              @update:active-key="(val) => modelCapabilityFilter = val as ModelCapabilityFilter"
            />

            <div class="model-items">
              <div v-for="entry in visibleProviderModels" :key="entry.index" class="model-row">
                <RrInput
                  v-model:value="entry.model.name"
                  placeholder="模型标识，例如 gpt-4o"
                  required
                />
                <RrSelect
                  :value="entry.model.capability"
                  :options="capabilitySelectOptions"
                  @update:value="(val) => onModelCapabilityChange(entry.model, val as AiMaterialCapability)"
                />
                <div class="default-radio-cell">
                  <RrRadio
                    :checked="entry.model.isDefault"
                    :name="`default-model-${entry.model.capability}`"
                    @change="setDefaultModel(entry.model)"
                  >
                    默认
                  </RrRadio>
                </div>
                <RrButton
                  type="text"
                  :danger="true"
                  size="small"
                  aria-label="删除模型"
                  title="删除模型"
                  @click="removeProviderModel(entry.index)"
                >
                  <Trash2 :size="15" />
                </RrButton>
              </div>
              <div v-if="visibleProviderModels.length === 0" class="model-empty">
                当前分类暂无模型
              </div>
            </div>
          </section>

          <div class="options-row">
            <RrCheckbox v-model:checked="providerForm.enabled">启用提供商</RrCheckbox>
            <RrCheckbox v-model:checked="providerForm.isDefault">设为默认</RrCheckbox>
          </div>

          <div class="form-actions">
            <RrButton @click="startCreateProvider">重置表单</RrButton>
            <RrButton
              type="primary"
              :loading="savingProvider"
              :disabled="!canSaveProvider"
              @click="saveProvider"
            >
              {{ editingProviderId == null ? '创建提供商' : '保存配置' }}
            </RrButton>
          </div>
        </RrForm>
      </main>
    </div>

    <!-- 删除提供商确认 -->
    <RrModal
      :open="providerToDelete != null"
      title="删除 AI 提供商"
      :width="420"
      ok-text="确定删除"
      cancel-text="取消"
      ok-type="primary"
      :ok-button-props="{ danger: true }"
      :confirm-loading="deletingProviderId != null"
      @update:open="(val) => { if (!val) providerToDelete = null }"
      @ok="confirmDeleteProvider"
      @cancel="providerToDelete = null"
    >
      <p class="delete-confirm-text">
        确定删除 AI 提供商“{{ providerToDelete?.name }}”吗？删除后该提供商的模型将无法继续用于素材生成。
      </p>
    </RrModal>
  </RrModal>
</template>

<style scoped>
.ai-providers-modal :deep(.rr-modal__body) {
  padding: 0;
}

.providers-modal-body {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  min-height: 520px;
  max-height: 72vh;
}

/* 左侧列表 */
.providers-list-panel {
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--rr-color-border);
  background: var(--rr-color-surface-subtle);
  padding: 20px 16px;
  overflow-y: auto;
}

.list-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 16px;
}

.list-head strong {
  font-size: 14px;
  font-weight: 600;
  color: var(--rr-color-text-strong);
}

.loading-state,
.empty-tip {
  padding: 36px 0;
  text-align: center;
  font-size: 13px;
  color: var(--rr-color-text-muted);
}

.provider-items {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.provider-item {
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 6px;
  border: 1px solid transparent;
  padding: 8px 8px;
  cursor: pointer;
  transition: border-color 150ms ease-out, background 150ms ease-out;
}

.provider-item:hover {
  background: var(--rr-color-surface);
}

.provider-item.active {
  border-color: var(--rr-color-primary-border);
  background: var(--rr-color-surface);
  box-shadow: var(--rr-shadow-1);
}

.provider-item.disabled {
  cursor: default;
}

.provider-badge {
  display: grid;
  width: 32px;
  height: 32px;
  place-items: center;
  border-radius: 6px;
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-strong);
  font-weight: 600;
  font-size: 13px;
  border: 1px solid var(--rr-color-border);
  flex-shrink: 0;
}

.provider-item.active .provider-badge {
  background: var(--rr-color-primary-soft);
  color: var(--rr-color-primary);
  border-color: var(--rr-color-primary-border);
}

.provider-meta {
  display: grid;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.provider-meta strong {
  font-size: 13px;
  font-weight: 600;
  color: var(--rr-color-text-strong);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.provider-meta small {
  font-size: 11px;
  color: var(--rr-color-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.provider-status-col {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

.test-btn {
  align-self: center;
  margin-right: 4px;
}

/* 右侧配置面板 */
.provider-form-panel {
  display: flex;
  flex-direction: column;
  background: var(--rr-color-surface);
  padding: 24px 28px;
  overflow-y: auto;
}

.form-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.form-head strong {
  font-size: 15px;
  font-weight: 600;
  color: var(--rr-color-text-strong);
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.grid-span-2 {
  grid-column: 1 / -1;
}

.form-grid label {
  display: grid;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: var(--rr-color-text);
}

.label-text {
  display: flex;
  align-items: center;
  gap: 4px;
}

.required-star {
  color: var(--rr-color-danger);
  font-style: normal;
}

/* 模型列表 */
.model-catalog-section {
  margin-top: 22px;
  border-top: 1px solid var(--rr-color-border);
  padding-top: 18px;
}

.model-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
}

.capability-tabs {
  margin: 8px 0 14px;
}

.model-items {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.model-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 130px 60px 32px;
  align-items: center;
  gap: 10px;
}

.default-radio-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.model-empty {
  padding: 20px 0;
  text-align: center;
  color: var(--rr-color-text-muted);
  font-size: 13px;
}

.options-row {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--rr-color-border);
}

.form-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid var(--rr-color-border);
}

.delete-confirm-text {
  font-size: 14px;
  line-height: 1.6;
  color: var(--rr-color-text);
  margin: 0;
}

@media (max-width: 768px) {
  .providers-modal-body {
    grid-template-columns: 1fr;
  }
  .providers-list-panel {
    border-right: 0;
    border-bottom: 1px solid var(--rr-color-border);
    max-height: 200px;
  }
}
</style>
